/**
 * svgEspace — outillage SVG du thème #10 Espace (4e, attendus 183-185).
 *
 * TROIS briques, toutes PAR CONSTRUCTION :
 *   1. Repère orthogonal 2D (O1/183) — repris du 5e (gen_945) avec les MÊMES
 *      classes de balisage (tx/ty/pt/pl/zl) pour que le solveur inverse
 *      pixels → coordonnées, ENRICHI d'une zone rectangulaire teintée + bornes
 *      d'inégalités (régions & inégalités, le neuf du 4e). Arithmétique interne
 *      en DIXIÈMES entiers (zéro flottant).
 *   2. Repère de l'espace 3D (O2/184) — pavé droit en perspective cavalière
 *      (réutilise paveNomme de svgSolides 5e) coté (arêtes AB, AE, BC) avec A
 *      pour origine. Figure AUTO-DESCRIPTIVE : un bloc <desc> encode le triplet
 *      (x ; y ; z) de chaque sommet → le solveur le relit (pas de lecture pixel
 *      3D, trop fragile). Convention : [AB) abscisses, [AD) ordonnées, [AE)
 *      altitudes ; A(0;0;0).
 *   3. Patron de pyramide (O3/185) — base polygonale régulière + n triangles
 *      rabattus, variantes correcte / face manquante / face recouvrante, pour
 *      les QCM d'images « est-ce un patron ? ». + formes simples (disque,
 *      triangle isocèle…) pour les vues de dessus/face d'un cône.
 *
 * Réutilise svgPyramide/svgCone (grandeurs-4e) et paveNomme/prismeTri/svgDisque
 * (svgSolides 5e) par import direct dans les générateurs.
 *
 * Revue 2026-09-23 (vague 4), AJOUTS (les briques ci-dessus sont inchangées,
 * sauf la face rose de patronPyramide, retirée) :
 *   4. svgRepereEspace : le repère de l'espace du cours (pavé ABCDEFGH, A
 *      origine, abscisses sur la fuyante, ordonnées sur l'horizontale,
 *      altitudes sur la verticale, « 0 » et « 1 » seulement, points en croix
 *      sur des arêtes visibles), avec ses tirages communs à 183 et 184.
 *   5. Les pyramides de 185 : patronPyramide185 (bases 3, 4 ou rectangle ;
 *      correct, manque, recouvre, aretes_inegales), pyramideNommee185 et
 *      pyramideDansCube185 (la pyramide GABC du cours), lettres en KaTeX_Math.
 *   Depuis cette revue, svgRepere3D, facesPave, formeSimple et frD ne servent
 *   plus à aucun gen (183, 184, 185 ne les importent plus) : à retirer dans un
 *   lot de nettoyage (ce lot n'avait le droit que d'ajouter).
 */

import { paveNomme } from '../../5eme/espace/svgSolides';
import { randInt, randomChoice, pickLettresSains, lettresRisquees } from '../../../utils';

// ═══════════════════════════════════════════════════════════
// Écriture française des dixièmes (identique à gen_945)
// ═══════════════════════════════════════════════════════════

export const frD = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};

const SUBS = '₁₂₃₄';
const subTspan = (n) => `z<tspan baseline-shift="sub" font-size="0.7em">${n}</tspan>`;

// ═══════════════════════════════════════════════════════════
// 1. Repère orthogonal 2D (O1) — copié de gen_945, + option `region`
// ═══════════════════════════════════════════════════════════

/**
 * Repère orthogonal carré [-bD ; bD]² (dixièmes), grille au pas pasD.
 * Graduations étiquetées aux entiers, posées EXACTEMENT à la position de leur
 * valeur (le solveur inverse la transformation depuis deux labels par axe).
 *   points : [{ xD, yD, label }]  — points nommés (cercle + label)
 *   zones  : true → quadrants z₁..z₄ teintés
 *   region : { xMinD, xMaxD, yMinD, yMaxD } → rectangle teinté + cadre pointillé
 *            (illustratif ; le solveur évalue les inégalités depuis le TEXTE)
 */
export const svgRepere2D = ({ bD, pasD, points = [], zones = false, region = null }) => {
    const PAD = 36, PLOT = 408;
    const W = PLOT + 2 * PAD;
    const range = 2 * bD;
    const px = (xD) => Math.round((PAD + ((xD + bD) / range) * PLOT) * 10) / 10;
    const py = (yD) => Math.round((PAD + ((bD - yD) / range) * PLOT) * 10) / 10;
    const x0 = px(0), y0 = py(0);
    const out = [];
    out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${W}" font-family="system-ui, sans-serif">`);
    out.push(`<rect x="0" y="0" width="${W}" height="${W}" fill="white"/>`);

    if (zones) {
        const ZONES = [
            { n: 1, sx: 1, sy: 1, fill: '#3b82f6' },
            { n: 2, sx: -1, sy: 1, fill: '#22c55e' },
            { n: 3, sx: -1, sy: -1, fill: '#f59e0b' },
            { n: 4, sx: 1, sy: -1, fill: '#ec4899' },
        ];
        ZONES.forEach((z) => {
            const xA = z.sx > 0 ? x0 : px(-bD);
            const yA = z.sy > 0 ? py(bD) : y0;
            const cx = xA + PLOT / 4, cy = yA + PLOT / 4;
            out.push(`<rect x="${xA}" y="${yA}" width="${PLOT / 2}" height="${PLOT / 2}" fill="${z.fill}" opacity="0.10"/>`);
            out.push(`<text class="zl" x="${cx}" y="${cy}" text-anchor="middle" font-size="21" font-weight="700" fill="#334155">${subTspan(SUBS[z.n - 1])}</text>`);
        });
    }

    if (region) {
        const xa = px(region.xMinD), xb = px(region.xMaxD);
        const ya = py(region.yMaxD), yb = py(region.yMinD);
        out.push(`<rect x="${Math.min(xa, xb)}" y="${Math.min(ya, yb)}" width="${Math.abs(xb - xa)}" height="${Math.abs(yb - ya)}" fill="#6366f1" opacity="0.14" stroke="#6366f1" stroke-width="1.4" stroke-dasharray="5 4"/>`);
    }

    // Quadrillage (la ligne 0 est portée par les axes)
    for (let v = -bD; v <= bD; v += pasD) {
        if (v === 0) continue;
        out.push(`<line x1="${px(v)}" y1="${py(-bD)}" x2="${px(v)}" y2="${py(bD)}" stroke="#e2e8f0" stroke-width="1"/>`);
        out.push(`<line x1="${px(-bD)}" y1="${py(v)}" x2="${px(bD)}" y2="${py(v)}" stroke="#e2e8f0" stroke-width="1"/>`);
    }
    // Axes + flèches
    out.push(`<line x1="${PAD - 8}" y1="${y0}" x2="${PAD + PLOT + 10}" y2="${y0}" stroke="#334155" stroke-width="1.6"/>`);
    out.push(`<polygon points="${PAD + PLOT + 16},${y0} ${PAD + PLOT + 7},${y0 - 4} ${PAD + PLOT + 7},${y0 + 4}" fill="#334155"/>`);
    out.push(`<line x1="${x0}" y1="${PAD + PLOT + 8}" x2="${x0}" y2="${PAD - 10}" stroke="#334155" stroke-width="1.6"/>`);
    out.push(`<polygon points="${x0},${PAD - 16} ${x0 - 4},${PAD - 7} ${x0 + 4},${PAD - 7}" fill="#334155"/>`);

    // Graduations étiquetées aux entiers ; « 0 » unique, décalé du croisement
    for (let v = -bD; v <= bD; v += 10) {
        if (v === 0) continue;
        out.push(`<line x1="${px(v)}" y1="${y0 - 4}" x2="${px(v)}" y2="${y0 + 4}" stroke="#334155" stroke-width="1"/>`);
        out.push(`<text class="tx" x="${px(v)}" y="${y0 + 17}" text-anchor="middle" font-size="12" fill="#1e293b">${frD(v)}</text>`);
        out.push(`<line x1="${x0 - 4}" y1="${py(v)}" x2="${x0 + 4}" y2="${py(v)}" stroke="#334155" stroke-width="1"/>`);
        out.push(`<text class="ty" x="${x0 - 7}" y="${py(v)}" text-anchor="end" dominant-baseline="middle" font-size="12" fill="#1e293b">${frD(v)}</text>`);
    }
    out.push(`<text x="${x0 - 7}" y="${y0 + 17}" text-anchor="end" font-size="12" fill="#1e293b">0</text>`);

    // Points nommés (cercle plein + label)
    points.forEach((p) => {
        const cx = px(p.xD), cy = py(p.yD);
        out.push(`<g class="pt"><circle cx="${cx}" cy="${cy}" r="4.5" fill="#1e3a8a"/><text class="pl" x="${cx + 8}" y="${cy - 8}" font-size="15" font-weight="700" fill="#1e3a8a">${p.label}</text></g>`);
    });

    out.push('</svg>');
    return out.join('');
};

// ═══════════════════════════════════════════════════════════
// 2. Repère de l'espace 3D (O2) — pavé coté + <desc> auto-descriptif
// ═══════════════════════════════════════════════════════════

/**
 * Pavé droit ABCDEFGH en perspective cavalière avec A pour origine.
 * Convention : [AB) = abscisses (x), [AD) = ordonnées (y), [AE) = altitudes (z).
 * Sommets : A(0;0;0) B(L;0;0) C(L;P;0) D(0;P;0) E(0;0;H) F(L;0;H) G(L;P;H) H(0;P;H).
 * Cotes affichées : AB = L, AE = H, BC = P (= AD, la profondeur).
 * Retourne { svg, coords, dims } — coords = { label: [x,y,z] } (entiers).
 * Le <desc> encode les coordonnées : le solveur les relit sans lecture pixel.
 */
export const svgRepere3D = ({ L, P, H, salt = 0 }) => {
    const coords = {
        A: [0, 0, 0], B: [L, 0, 0], C: [L, P, 0], D: [0, P, 0],
        E: [0, 0, H], F: [L, 0, H], G: [L, P, H], H: [0, P, H],
    };
    const { svg } = paveNomme(
        { L, H, P },
        {
            vb: { w: 280, h: 240 }, salt,
            coter: { L: `${L}`, H: `${H}`, P: `${P}` },
        },
    );
    const desc = `<desc>coords3d:${Object.entries(coords).map(([k, v]) => `${k}=${v.join(';')}`).join('|')}|dims=${L};${P};${H}</desc>`;
    const svgDesc = svg.replace(/(<svg[^>]*>)/, `$1${desc}`);
    return { svg: svgDesc, coords, dims: { L, P, H } };
};

/** Les 6 faces d'un pavé (L;P;H) → { nom lisible : test d'appartenance }. */
export const facesPave = ({ L, P, H }) => [
    { nom: 'ABCD', desc: 'z = 0', test: (p) => p[2] === 0 },
    { nom: 'EFGH', desc: `z = ${H}`, test: (p) => p[2] === H },
    { nom: 'ABFE', desc: 'y = 0', test: (p) => p[1] === 0 },
    { nom: 'DCGH', desc: `y = ${P}`, test: (p) => p[1] === P },
    { nom: 'ADHE', desc: 'x = 0', test: (p) => p[0] === 0 },
    { nom: 'BCGF', desc: `x = ${L}`, test: (p) => p[0] === L },
];

// ═══════════════════════════════════════════════════════════
// 3a. Patron de pyramide (O3) — base régulière + n triangles rabattus
// ═══════════════════════════════════════════════════════════

const NOIR = '#1f2937';
const BLEU = '#bfdbfe';
const R1 = (v) => Number(v).toFixed(1);

/**
 * Patron d'une pyramide à base régulière à n côtés.
 * variante : 'correct' | 'manque' (une face latérale absente)
 *          | 'recouvre' (une face rabattue SUR la base = recouvrement)
 */
export const patronPyramide = (n, variante = 'correct', opts = {}) => {
    const { vb = { w: 200, h: 200 }, salt = 0 } = opts;
    const cx = vb.w / 2, cy = vb.h / 2;
    const rBase = Math.min(vb.w, vb.h) * (n <= 4 ? 0.2 : 0.24);
    const slant = rBase * (n <= 4 ? 1.15 : 0.9);
    // Sommets de la base (un côté « au sud » pour n pair)
    const start = -Math.PI / 2 - Math.PI / n;
    const V = [];
    for (let i = 0; i < n; i++) {
        const a = start + (i * 2 * Math.PI) / n;
        V.push({ x: cx + rBase * Math.cos(a), y: cy + rBase * Math.sin(a) });
    }
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" data-v="${salt}">`];
    out.push(`<rect x="0" y="0" width="${vb.w}" height="${vb.h}" fill="white"/>`);
    // Base
    out.push(`<polygon points="${V.map((p) => `${R1(p.x)},${R1(p.y)}`).join(' ')}" fill="${BLEU}" stroke="${NOIR}" stroke-width="1.6" stroke-linejoin="round"/>`);
    // Faces latérales (triangles rabattus vers l'extérieur)
    for (let i = 0; i < n; i++) {
        if (variante === 'manque' && i === n - 1) continue;
        const A = V[i], B = V[(i + 1) % n];
        const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2;
        let nx = mx - cx, ny = my - cy;
        const len = Math.hypot(nx, ny) || 1;
        nx /= len; ny /= len;
        // 'recouvre' : la face 0 est rabattue vers l'INTÉRIEUR (apex vers le centre)
        const sens = (variante === 'recouvre' && i === 0) ? -1 : 1;
        const apex = { x: mx + sens * nx * slant, y: my + sens * ny * slant };
        // Revue 2026-09-23 (vague 4) : plus de face rose. La face rabattue était
        // la seule colorée, dans 100 % des « recouvre » et jamais ailleurs : la
        // couleur donnait la réponse (gen_185 estpatron, gen_1240 estpatron).
        const fill = 'none';
        out.push(`<polygon points="${R1(A.x)},${R1(A.y)} ${R1(B.x)},${R1(B.y)} ${R1(apex.x)},${R1(apex.y)}" fill="${fill}" stroke="${NOIR}" stroke-width="1.4" stroke-linejoin="round"/>`);
    }
    out.push('</svg>');
    return out.join('');
};

// ═══════════════════════════════════════════════════════════
// 3b. Formes simples 2D (vues de dessus / de face d'un cône)
// ═══════════════════════════════════════════════════════════

/** type : 'disque' | 'triangleIso' | 'rectangle' | 'carre'. */
export const formeSimple = (type, opts = {}) => {
    const { vb = { w: 150, h: 140 }, salt = 0 } = opts;
    const cx = vb.w / 2, cy = vb.h / 2;
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" data-v="${salt}">`];
    out.push(`<rect x="0" y="0" width="${vb.w}" height="${vb.h}" fill="white"/>`);
    if (type === 'disque') {
        const r = Math.min(vb.w, vb.h) * 0.34;
        out.push(`<circle cx="${cx}" cy="${cy}" r="${R1(r)}" fill="${BLEU}" stroke="${NOIR}" stroke-width="1.8"/>`);
        out.push(`<circle cx="${cx}" cy="${cy}" r="2.2" fill="${NOIR}"/>`);
    } else if (type === 'triangleIso') {
        const b = vb.w * 0.5, h = vb.h * 0.6;
        out.push(`<polygon points="${R1(cx)},${R1(cy - h / 2)} ${R1(cx - b / 2)},${R1(cy + h / 2)} ${R1(cx + b / 2)},${R1(cy + h / 2)}" fill="${BLEU}" stroke="${NOIR}" stroke-width="1.8" stroke-linejoin="round"/>`);
    } else if (type === 'carre') {
        const a = Math.min(vb.w, vb.h) * 0.5;
        out.push(`<rect x="${R1(cx - a / 2)}" y="${R1(cy - a / 2)}" width="${R1(a)}" height="${R1(a)}" fill="${BLEU}" stroke="${NOIR}" stroke-width="1.8"/>`);
    } else { // rectangle
        const w = vb.w * 0.6, h = vb.h * 0.4;
        out.push(`<rect x="${R1(cx - w / 2)}" y="${R1(cy - h / 2)}" width="${R1(w)}" height="${R1(h)}" fill="${BLEU}" stroke="${NOIR}" stroke-width="1.8"/>`);
    }
    out.push('</svg>');
    return out.join('');
};

// ═══════════════════════════════════════════════════════════
// 4. Repère de l'espace du cours (183, 184) : revue 2026-09-23 (vague 4)
// ═══════════════════════════════════════════════════════════
//
// La figure du cours (19Espace2 p. 4-5) et d'Eduscol (attendus de fin de 4e,
// p. 9) : un pavé droit ABCDEFGH en perspective cavalière (k = 0,5 ; 30°), A
// origine au sommet arrière bas gauche, [AB) FUYANTE vers l'élève pour les
// abscisses (rouge), [AD) horizontale pour les ordonnées (vert), [AE)
// verticale pour les altitudes (bleu). Les trois arêtes cachées sont celles de
// A : elles sont portées par les axes, tracés pleins et fléchés comme dans le
// cours, prolongés au-delà de B, D et E. Une graduation par unité sur chaque
// axe ; « 0 » en A et « 1 » sur chaque axe SEULEMENT : les nombres au bout des
// axes (le cours écrit 5, 7 et 4) sont les coordonnées de B, D et E, la figure
// donnerait la réponse (mode auto, À ARBITRER). Jamais de cote.
// Un point est une croix posée sur une arête VISIBLE : deux de ses coordonnées
// sont celles des extrémités de l'arête, la troisième se compte sur l'arête,
// graduée elle aussi (le quadrillage d'Eduscol). En projection, l'axe des
// ordonnées coupe [CG] et celui des altitudes coupe [FG] : un point posé sur
// un de ces croisements se lirait aussi sur l'axe, d'où une marge. La
// décision disait 8 unités au moins ; regardée en PNG, une croix à 9 unités
// d'un croisement (ses bras font 4,5) touche presque l'axe et se lit dessus :
// marge portée à 16 unités (20 pour les petites images de choix, affichées
// réduites).
// Le cadrage est calculé sur tout ce qui est dessiné, lettres comprises : rien
// ne sort du viewBox (vérifié, throw sinon). Chaque étiquette est essayée
// autour de son point, loin des traits et des autres étiquettes.

const K_CAV = 0.5;
const COS_30 = Math.cos(Math.PI / 6);
const SIN_30 = 0.5;
const ENCRE = '#1f2937';
const ENCRE_POINT = '#1E3A8A';
const POLICE_MATH = "KaTeX_Math, 'Times New Roman', serif";
export const COULEURS_AXES = { x: '#DC2626', y: '#16A34A', z: '#2563EB' };

/** Coordonnées des sommets du cours : A(0 ; 0 ; 0), B sur la fuyante, D sur l'horizontale, E sur la verticale. */
export const sommetsRepere = (X, Y, Z) => ({
    A: [0, 0, 0], B: [X, 0, 0], C: [X, Y, 0], D: [0, Y, 0],
    E: [0, 0, Z], F: [X, 0, Z], G: [X, Y, Z], H: [0, Y, Z],
});

/** Les neuf arêtes visibles ; [AB], [AD] et [AE], cachées, sont portées par les axes. */
export const ARETES_VISIBLES = ['BC', 'CD', 'DH', 'EH', 'EF', 'BF', 'FG', 'GH', 'CG'];

/** L'arête visible qui porte le point c STRICTEMENT entre ses extrémités, ou null. */
export const areteVisible = (X, Y, Z, c) => {
    const S = sommetsRepere(X, Y, Z);
    const nom = ARETES_VISIBLES.find((a) => {
        const P = S[a[0]]; const Q = S[a[1]];
        const i = [0, 1, 2].find((k) => P[k] !== Q[k]);
        return [0, 1, 2].every((k) => k === i || c[k] === P[k])
            && c[i] > Math.min(P[i], Q[i]) && c[i] < Math.max(P[i], Q[i]);
    });
    return nom || null;
};

/**
 * Unité du dessin (unités du viewBox) : la figure d'énoncé tient dans environ
 * 420 × 330. Une image de choix est affichée à 144 px de haut au plus
 * (ChoixImageEleve) : son unité est FIXE et petite, pour que traits, croix et
 * nombres restent gros par rapport au dessin une fois l'image réduite.
 */
export const echelleRepere = (X, Y, Z, choix = false) => {
    if (choix) return 28;
    const s = Math.min(300 / (Y + K_CAV * COS_30 * X), 230 / (Z + K_CAV * SIN_30 * X));
    return Math.round(Math.max(28, Math.min(52, s)));
};

/** Projection cavalière, A à l'origine, l'axe vertical de l'écran vers le bas. */
const projeterRepere = (s) => (c) => ({ x: s * (c[1] - K_CAV * COS_30 * c[0]), y: s * (-c[2] + K_CAV * SIN_30 * c[0]) });

/** Les deux croisements de la projection : axe des ordonnées × [CG], axe des altitudes × [FG]. */
export const croisementsRepere = (X, Y, Z, choix = false) => {
    const s = echelleRepere(X, Y, Z, choix);
    return [
        { x: s * (Y - K_CAV * COS_30 * X), y: 0 },
        { x: 0, y: s * (-Z + K_CAV * SIN_30 * X) },
    ];
};

// ─── Géométrie du placement des étiquettes ───
const distPointSegment = (p, a, b) => {
    const dx = b.x - a.x; const dy = b.y - a.y; const L2 = dx * dx + dy * dy;
    const t = L2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2)) : 0;
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};
const orient = (p, q, r) => Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
const segmentsSeCoupent = (a, b, c, d) => orient(a, b, c) !== orient(a, b, d) && orient(c, d, a) !== orient(c, d, b);
const distPointBoite = (p, bx) => Math.hypot(Math.max(bx.x0 - p.x, 0, p.x - bx.x1), Math.max(bx.y0 - p.y, 0, p.y - bx.y1));
const distSegmentBoite = (a, b, bx) => {
    const dedans = (p) => p.x >= bx.x0 && p.x <= bx.x1 && p.y >= bx.y0 && p.y <= bx.y1;
    if (dedans(a) || dedans(b)) return 0;
    const coins = [{ x: bx.x0, y: bx.y0 }, { x: bx.x1, y: bx.y0 }, { x: bx.x1, y: bx.y1 }, { x: bx.x0, y: bx.y1 }];
    if (coins.some((c, i) => segmentsSeCoupent(a, b, c, coins[(i + 1) % 4]))) return 0;
    return Math.min(...coins.map((c) => distPointSegment(c, a, b)), distPointBoite(a, bx), distPointBoite(b, bx));
};
const distBoites = (u, v) => Math.hypot(Math.max(u.x0 - v.x1, 0, v.x0 - u.x1), Math.max(u.y0 - v.y1, 0, v.y0 - u.y1));
/**
 * Un point se lit sans ambiguïté : sur une arête visible, loin des deux
 * croisements (16 unités ; 20 dans une image de choix, à son unité) et loin
 * des axes eux-mêmes (12 unités ; 16) : regardée en PNG, une croix de [CD]
 * voisine de D touchait presque l'axe des ordonnées, qui passe par D.
 */
export const pointLisible = (X, Y, Z, c, choix = false) => {
    if (!areteVisible(X, Y, Z, c)) return false;
    const s = echelleRepere(X, Y, Z, choix);
    const P = projeterRepere(s)(c);
    const loinCroisements = croisementsRepere(X, Y, Z, choix).every((K) => Math.hypot(P.x - K.x, P.y - K.y) >= (choix ? 20 : 16));
    const bouts = [[X + 1, 0, 0], [0, Y + 1, 0], [0, 0, Z + 1]].map(projeterRepere(s));
    const loinAxes = bouts.every((B) => distPointSegment(P, { x: 0, y: 0 }, B) >= (choix ? 16 : 12));
    return loinCroisements && loinAxes;
};

/** Boîte d'un texte centré en (cx, cy) : largeur prudente (lettres italiques larges), hauteur des capitales. */
const boiteTexte = (cx, cy, texte, fs) => {
    const hw = (0.33 * texte.length + 0.12) * fs; const hh = 0.4 * fs;
    return { x0: cx - hw, x1: cx + hw, y0: cy - hh, y1: cy + hh };
};
/** Première position dégagée (3 unités de blanc au moins) autour de l'ancre, en partant de la direction préférée. */
const placerEtiquette = (ancre, texte, fs, prefDeg, rayon, traits, boites) => {
    let meilleur = null;
    for (let r = rayon; r <= rayon + 12; r += 3) {
        for (let k = 0; k <= 12; k++) {
            for (const signe of (k === 0 ? [1] : [1, -1])) {
                const t = ((prefDeg + signe * k * 15) * Math.PI) / 180;
                const cx = ancre.x + r * Math.cos(t); const cy = ancre.y + r * Math.sin(t);
                const bx = boiteTexte(cx, cy, texte, fs);
                const marge = Math.min(
                    ...traits.map((tr) => distSegmentBoite(tr.a, tr.b, bx) - tr.w / 2),
                    ...boites.map((b) => distBoites(bx, b)),
                );
                if (marge >= 3) return { cx, cy, bx, marge };
                if (!meilleur || marge > meilleur.marge) meilleur = { cx, cy, bx, marge };
            }
        }
    }
    return meilleur;
};

/**
 * Le repère de l'espace du cours.
 * @param {{ X: number, Y: number, Z: number,
 *           points?: Array<{ nom: string, c: number[] }>,
 *           sommets?: boolean, choix?: boolean }} cfg
 *   X, Y, Z : longueurs de [AB], [AD], [AE] en unités (entiers) ;
 *   points : croix sur des arêtes VISIBLES (nom '' : croix sans lettre), throw sinon ;
 *   sommets : false retire les lettres des sommets ;
 *   choix : petite image de QCM (traits et nombres plus épais, cadre serré,
 *           marge de 20 aux croisements ; le cadre ne dépend pas de la croix,
 *           les quatre images d'un même pavé ont le même) ;
 *   graduer : arêtes graduées en plus de celles qui portent un point ;
 *   eviter : positions (coordonnées) de croix que les étiquettes évitent aussi.
 *   Avec les mêmes graduer et eviter, les images de choix d'un même pavé ne
 *   diffèrent QUE par leur croix (étiquettes et graduations identiques).
 * @returns {{ svg: string, alt: string, echelle: number, margeEtiquettes: number }}
 *   margeEtiquettes : le plus petit blanc (unités du viewBox) entre une étiquette
 *   et un trait ou une autre étiquette (3 visé ; moins = placement au mieux).
 */
export const svgRepereEspace = ({ X, Y, Z, points = [], sommets = true, choix = false, graduer = [], eviter = [] }) => {
    const s = echelleRepere(X, Y, Z, choix);
    const P = projeterRepere(s);
    const S = sommetsRepere(X, Y, Z);
    const E = {};
    Object.keys(S).forEach((k) => { E[k] = P(S[k]); });
    points.forEach((pt) => {
        if (!pointLisible(X, Y, Z, pt.c, choix)) {
            throw new Error(`svgRepereEspace : point illisible ${pt.nom} (${pt.c.join(' ; ')}) dans le pavé ${X} × ${Y} × ${Z}`);
        }
    });
    const ep = choix ? 1.3 : 1;
    const axes = []; const aretes = []; const gradAretes = []; const croix = []; const fleches = []; const textes = [];

    // Axes : de A jusqu'au-delà du sommet, flèche, une graduation par unité.
    const AXES = [
        { e: 'x', n: X, d: { x: -COS_30, y: SIN_30 }, sommet: 'B', unite: K_CAV * s },
        { e: 'y', n: Y, d: { x: 1, y: 0 }, sommet: 'D', unite: s },
        { e: 'z', n: Z, d: { x: 0, y: -1 }, sommet: 'E', unite: s },
    ];
    // Une image de choix est affichée à 144 px de haut au plus : axes moins prolongés.
    const PROLONGE = choix ? 18 : 30; const FLECHE = choix ? 8 : 10; const DEMI_GRAD = choix ? 5 : 4;
    AXES.forEach((ax) => {
        const V = E[ax.sommet]; const { d } = ax; const nrm = { x: -d.y, y: d.x };
        const bout = { x: V.x + PROLONGE * d.x, y: V.y + PROLONGE * d.y };
        ax.pointe = { x: V.x + (PROLONGE + FLECHE) * d.x, y: V.y + (PROLONGE + FLECHE) * d.y };
        axes.push({ a: { x: 0, y: 0 }, b: bout, w: 2 * ep, couleur: COULEURS_AXES[ax.e], classe: `axe-${ax.e}` });
        fleches.push({
            pts: [ax.pointe, { x: bout.x + 4.5 * nrm.x, y: bout.y + 4.5 * nrm.y }, { x: bout.x - 4.5 * nrm.x, y: bout.y - 4.5 * nrm.y }],
            couleur: COULEURS_AXES[ax.e], classe: `fleche-${ax.e}`,
        });
        for (let k = 1; k <= ax.n; k++) {
            const T = { x: k * ax.unite * d.x, y: k * ax.unite * d.y };
            axes.push({ a: { x: T.x - DEMI_GRAD * nrm.x, y: T.y - DEMI_GRAD * nrm.y }, b: { x: T.x + DEMI_GRAD * nrm.x, y: T.y + DEMI_GRAD * nrm.y }, w: 1.5 * ep, couleur: COULEURS_AXES[ax.e], classe: `g${ax.e}` });
        }
        ax.T1 = { x: ax.unite * d.x, y: ax.unite * d.y };
    });

    // Les neuf arêtes visibles.
    ARETES_VISIBLES.forEach((a) => aretes.push({ a: E[a[0]], b: E[a[1]], w: 1.6 * ep, couleur: ENCRE, classe: 'arete' }));

    // L'arête qui porte un point est graduée, sauf là où est la croix (une image
    // de choix garde toutes ses graduations : les quatre images restent égales).
    const occupe = new Map();
    graduer.forEach((a) => { if (!occupe.has(a)) occupe.set(a, new Set()); });
    points.forEach((pt) => {
        const a = areteVisible(X, Y, Z, pt.c);
        if (!occupe.has(a)) occupe.set(a, new Set());
        if (!choix) occupe.get(a).add(pt.c.join(';'));
    });
    occupe.forEach((pris, a) => {
        const A0 = S[a[0]]; const B0 = S[a[1]];
        const i = [0, 1, 2].find((k) => A0[k] !== B0[k]);
        const pa = P(A0); const pb = P(B0);
        const L = Math.hypot(pb.x - pa.x, pb.y - pa.y);
        const nrm = { x: -(pb.y - pa.y) / L, y: (pb.x - pa.x) / L };
        for (let v = Math.min(A0[i], B0[i]) + 1; v < Math.max(A0[i], B0[i]); v++) {
            const c = [...A0]; c[i] = v;
            if (!pris.has(c.join(';'))) {
                const T = P(c);
                gradAretes.push({ a: { x: T.x - 3.5 * nrm.x, y: T.y - 3.5 * nrm.y }, b: { x: T.x + 3.5 * nrm.x, y: T.y + 3.5 * nrm.y }, w: 1.3 * ep, couleur: ENCRE, classe: 'ga' });
            }
        }
    });

    // Les croix (et, pour le placement des étiquettes seulement, celles à éviter).
    const brasCroix = (c) => {
        const Q = P(c); const r = choix ? 6 : 4.5;
        return [
            { a: { x: Q.x - r, y: Q.y - r }, b: { x: Q.x + r, y: Q.y + r }, w: 2 * ep, couleur: ENCRE_POINT, classe: 'croix' },
            { a: { x: Q.x - r, y: Q.y + r }, b: { x: Q.x + r, y: Q.y - r }, w: 2 * ep, couleur: ENCRE_POINT, classe: 'croix' },
        ];
    };
    points.forEach((pt) => croix.push(...brasCroix(pt.c)));
    const croixEvitees = eviter.flatMap(brasCroix);

    // Étiquettes : lettres des axes, « 1 », « 0 », sommets, points.
    const obstacles = [
        ...axes, ...aretes, ...gradAretes, ...croix, ...croixEvitees,
        ...fleches.flatMap((fl) => [[0, 1], [1, 2], [2, 0]].map(([i, j]) => ({ a: fl.pts[i], b: fl.pts[j], w: 1 }))),
    ];
    const boites = [];
    const poser = (ancre, texte, fs, pref, rayon, style) => {
        const pos = placerEtiquette(ancre, texte, fs, pref, rayon, obstacles, boites);
        boites.push(pos.bx);
        textes.push({ ...style, texte, fs, cx: pos.cx, cy: pos.cy, bx: pos.bx, marge: pos.marge });
    };
    const angle = (v) => (Math.atan2(v.y, v.x) * 180) / Math.PI;
    AXES.forEach((ax) => poser(ax.pointe, ax.e, 17, angle(ax.d), 9, { couleur: COULEURS_AXES[ax.e], math: true, classe: 'lettre-axe' }));
    const fsN = choix ? 15 : 13;
    const PREF_UN = { x: 60, y: -90, z: 180 };            // sous la fuyante, au-dessus de l'horizontale, à gauche de la verticale (cours)
    AXES.forEach((ax) => poser(ax.T1, '1', fsN, PREF_UN[ax.e], 10, { couleur: COULEURS_AXES[ax.e], classe: `un-${ax.e}` }));
    poser({ x: 0, y: 0 }, '0', fsN, -45, 10, { couleur: ENCRE, classe: 'zero' });
    if (sommets) {
        const PREF = { A: 210, B: 100, C: 60, D: 60, E: 210, F: 210, G: 240, H: -45 };
        Object.keys(S).forEach((k) => poser(E[k], k, 16, PREF[k], 12, { couleur: ENCRE, math: true, classe: 'sommet' }));
    }
    const centre = P([X / 2, Y / 2, Z / 2]);
    points.filter((pt) => pt.nom).forEach((pt) => {
        const Q = P(pt.c);
        poser(Q, pt.nom, 16, angle({ x: Q.x - centre.x, y: Q.y - centre.y }), 13, { couleur: ENCRE_POINT, math: true, classe: 'point' });
    });

    // Cadrage : tout ce qui est dessiné (les croix et les graduations d'arête
    // exceptées pour une image de choix, pour que les quatre cadres soient égaux).
    const MARGE = 8;
    const xs = []; const ys = [];
    const prendre = (liste) => liste.forEach((t) => [t.a, t.b].forEach((p) => { xs.push(p.x - t.w / 2, p.x + t.w / 2); ys.push(p.y - t.w / 2, p.y + t.w / 2); }));
    prendre(axes); prendre(aretes);
    if (!choix) { prendre(gradAretes); prendre(croix); }
    fleches.forEach((fl) => fl.pts.forEach((p) => { xs.push(p.x); ys.push(p.y); }));
    textes.forEach((t) => { xs.push(t.bx.x0, t.bx.x1); ys.push(t.bx.y0, t.bx.y1); });
    const minX = Math.min(...xs); const maxX = Math.max(...xs); const minY = Math.min(...ys); const maxY = Math.max(...ys);
    const W = Math.ceil(Math.max(choix ? 0 : 420, maxX - minX + 2 * MARGE));
    const H = Math.ceil(maxY - minY + 2 * MARGE);
    const ox = (W - (maxX - minX)) / 2 - minX; const oy = MARGE - minY;
    // Vérification : rien hors du cadre, croix comprises.
    const horsCadre = (x, y) => x < 0 || y < 0 || x > W || y > H;
    [...axes, ...aretes, ...gradAretes, ...croix].forEach((t) => [t.a, t.b].forEach((p) => {
        if (horsCadre(p.x + ox, p.y + oy)) throw new Error(`svgRepereEspace : trait hors du cadre (${X} × ${Y} × ${Z})`);
    }));
    textes.forEach((t) => {
        if (horsCadre(t.bx.x0 + ox, t.bx.y0 + oy) || horsCadre(t.bx.x1 + ox, t.bx.y1 + oy)) throw new Error(`svgRepereEspace : texte « ${t.texte} » hors du cadre`);
    });

    const f = (v) => String(Math.round(v * 10) / 10);
    const ligne = (t) => `<line class="${t.classe}" x1="${f(t.a.x + ox)}" y1="${f(t.a.y + oy)}" x2="${f(t.b.x + ox)}" y2="${f(t.b.y + oy)}" stroke="${t.couleur}" stroke-width="${f(t.w)}" stroke-linecap="round"/>`;
    const fleche = (fl) => `<polygon class="${fl.classe}" points="${fl.pts.map((p) => `${f(p.x + ox)},${f(p.y + oy)}`).join(' ')}" fill="${fl.couleur}"/>`;
    const texte = (t) => {
        const y = f(t.cy + 0.35 * t.fs + oy);
        const contenu = t.math ? `<tspan font-family="${POLICE_MATH}" font-style="italic">${t.texte}</tspan>` : t.texte;
        return `<text class="${t.classe}" x="${f(t.cx + ox)}" y="${y}" font-size="${t.fs}" text-anchor="middle" fill="${t.couleur}">${contenu}</text>`;
    };
    const nommes = points.filter((pt) => pt.nom);
    const dataPoints = [...Object.keys(S).map((k) => `${k}:${S[k].join(',')}`), ...nommes.map((pt) => `${pt.nom}:${pt.c.join(',')}`)].join('|');
    const alt = `Pavé droit${sommets ? ' ABCDEFGH' : ''} dessiné en perspective cavalière. Trois axes gradués partent du sommet${sommets ? ' A' : ' caché'} : `
        + `l'axe x le long de ${sommets ? 'l\'arête [AB]' : 'la fuyante'}, l'axe y le long de ${sommets ? 'l\'arête [AD]' : 'l\'horizontale'}, l'axe z le long de ${sommets ? 'l\'arête [AE]' : 'la verticale'} ; `
        + '0 est écrit à l\'origine et 1 sur chaque axe.'
        + nommes.map((pt) => ` Le point ${pt.nom} est marqué d'une croix sur l'arête [${areteVisible(X, Y, Z, pt.c)}], graduée.`).join('');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" `
        + `data-repere="${X};${Y};${Z}" data-points="${dataPoints}" role="img" aria-label="${alt.replace(/"/g, '&quot;')}">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`
        + axes.map(ligne).join('') + fleches.map(fleche).join('')
        + aretes.map(ligne).join('') + gradAretes.map(ligne).join('') + croix.map(ligne).join('')
        + textes.map(texte).join('')
        + '</svg>';
    return { svg, alt, echelle: s, margeEtiquettes: Math.min(...textes.map((t) => t.marge)) };
};

/** Élément d'énoncé : la figure en data-URL dans imageUrl (jamais imageData, §12), avec son alt. */
export const elementFigure = (svg, alt, maxWidth = 'medium') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, alt, maxWidth } }],
});

/** Choix image d'un QCM (§1 : imageData obligatoire dans un choix). */
export const choixFigure = (svg) => ({ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` } });

// ─── Tirages communs à 183 et 184 (revue 2026-09-23, vague 4) ───

/** Dimensions du pavé du repère : X (fuyante) de 2 à 4, Y de 3 à 7, Z de 2 à 5, deux à deux distinctes. */
export const tirerDimsRepere = () => {
    for (let essai = 0; essai < 60; essai++) {
        const X = randInt(2, 4); const Y = randInt(3, 7); const Z = randInt(2, 5);
        if (X !== Y && Y !== Z && X !== Z) return { X, Y, Z };
    }
    return { X: 2, Y: 5, Z: 4 };                       // le pavé d'Eduscol
};

/**
 * n noms de points, jamais un sommet : pickLettresSains, puis la même garde
 * sur TOUTES les lettres affichées (les huit sommets en plus) : T est exclu
 * d'office (T et G), P aussi (P et D).
 */
const POOL_POINTS = ['K', 'L', 'M', 'N', 'R', 'S', 'U', 'V'];
export const nommerPointsRepere = (n) => {
    for (let essai = 0; essai < 40; essai++) {
        const noms = pickLettresSains(POOL_POINTS, n);
        if (!lettresRisquees([...'ABCDEFGH', ...noms])) return noms;
    }
    return ['K', 'L', 'M'].slice(0, n);                // sûr avec A à H (vérifié par le test jetable)
};

/** Tous les points lisibles des arêtes visibles, à coordonnée libre ENTIÈRE : [{ c, arete }]. */
export const pointsAretesRepere = ({ X, Y, Z }, choix = false) => {
    const S = sommetsRepere(X, Y, Z);
    const out = [];
    ARETES_VISIBLES.forEach((a) => {
        const P = S[a[0]]; const Q = S[a[1]];
        const i = [0, 1, 2].find((k) => P[k] !== Q[k]);
        for (let v = Math.min(P[i], Q[i]) + 1; v < Math.max(P[i], Q[i]); v++) {
            const c = [...P]; c[i] = v;
            if (pointLisible(X, Y, Z, c, choix)) out.push({ c, arete: a });
        }
    });
    return out;
};

/** Une coordonnée en LaTeX : 3{,}5 pour une demie. */
export const coordLatex = (v) => String(v).replace('.', '{,}');
/** Un triplet en LaTeX, espacé comme le cours : (5 ; 3,5 ; 4). */
export const tripletLatex = (c) => `(${c.map(coordLatex).join('\\,;\\,')})`;

/**
 * Un point à trois coordonnées DISTINCTES et NON NULLES (G, ou un point lisible
 * de [FG], [CG] ou [GH], les arêtes dont les deux coordonnées fixes sont des
 * maxima), l'ORDRE de ses trois valeurs tiré d'abord, uniformément parmi les
 * six. Sans ce tirage, les plages du pavé (X de 2 à 4, Y de 3 à 7) faisaient de
 * l'ordonnée la plus grande valeur dans 78 % des tirages et de l'abscisse la
 * plus petite dans 61 % : la grandeur seule donnait la place (mesuré sur 183).
 * @returns {{ dims, c: number[], arete: string|null, sommet: string|null }}
 */
const ORDRES = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]];
export const tirerPointTroisValeurs = () => {
    const ordre = randomChoice(ORDRES).join();
    const rangs = (c) => c.map((v) => c.filter((w) => w < v).length).join();
    for (let essai = 0; essai < 80; essai++) {
        const dims = tirerDimsRepere();
        const { X, Y, Z } = dims;
        const cands = [{ c: [X, Y, Z], arete: null, sommet: 'G' },
            ...pointsAretesRepere(dims).filter((q) => ['FG', 'CG', 'GH'].includes(q.arete)).map((q) => ({ ...q, sommet: null }))]
            .filter((q) => new Set(q.c).size === 3 && !q.c.includes(0) && rangs(q.c) === ordre);
        if (cands.length > 0) return { dims, ...randomChoice(cands) };
    }
    const dims = tirerDimsRepere();
    return { dims, c: [dims.X, dims.Y, dims.Z], arete: null, sommet: 'G' };
};

// ═══════════════════════════════════════════════════════════
// 5. Pyramides de 185 : patrons et perspectives nommées (revue 2026-09-23, vague 4)
// ═══════════════════════════════════════════════════════════

/**
 * Sommets de la base, en cm, centre à l'origine, un côté horizontal en bas (y
 * vers le bas, comme l'écran) : triangle équilatéral de côté a, carré de côté
 * a, rectangle a × b (a horizontal).
 */
const baseEnCm185 = (base, a, b = a) => {
    if (base === 'triangle') {
        const r = a / Math.sqrt(3);
        return [{ x: -a / 2, y: r / 2 }, { x: a / 2, y: r / 2 }, { x: 0, y: -r }];
    }
    return [{ x: -a / 2, y: b / 2 }, { x: a / 2, y: b / 2 }, { x: a / 2, y: -b / 2 }, { x: -a / 2, y: -b / 2 }];
};

/** Rayon du cercle circonscrit à la base : une pyramide n'existe que si l'arête latérale le dépasse. */
export const rayonBase185 = (base, a, b = a) => Math.max(...baseEnCm185(base, a, b).map((p) => Math.hypot(p.x, p.y)));

/**
 * Géométrie d'un patron, en cm : la base, puis une face isocèle par côté,
 * dessinée vers l'extérieur, rabattue vers l'intérieur (« recouvre »), absente
 * (« manque ») ; « aretes_inegales » (base carrée) : les faces `face` et
 * `face + 2`, qui se font face, ont l'arête latérale L2, les deux autres L.
 */
const geometriePatron185 = ({ base, a, b = a, L, L2 = null, variante = 'correct', face = 0 }) => {
    const B = baseEnCm185(base, a, b); const n = B.length;
    const faces = [];
    B.forEach((P, i) => {
        if (variante === 'manque' && i === face) return;
        const Q = B[(i + 1) % n];
        const m = { x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 };
        const d = Math.hypot(m.x, m.y) || 1;
        const demi = Math.hypot(Q.x - P.x, Q.y - P.y) / 2;
        const l = variante === 'aretes_inegales' && (i === face || i === (face + 2) % n) ? L2 : L;
        const sens = variante === 'recouvre' && i === face ? -1 : 1;
        const h = Math.sqrt(l * l - demi * demi);
        faces.push({ i, P, Q, rabattue: sens < 0, apex: { x: m.x + (sens * m.x * h) / d, y: m.y + (sens * m.y * h) / d } });
    });
    return { B, faces };
};

/** Le demi-côté (cm) du carré centré qui contient le patron : le patron remplit son cadre. */
const etenduePatron185 = (params) => {
    const { B, faces } = geometriePatron185(params);
    return Math.max(...[...B, ...faces.map((f) => f.apex)].map((p) => Math.max(Math.abs(p.x), Math.abs(p.y))));
};

/** Les segments dessinés, côtés de la base puis arêtes latérales, avec leur longueur en cm. */
const segmentsPatron185 = ({ B, faces }) => [
    ...B.map((P, i) => ({ P, Q: B[(i + 1) % B.length], base: true })),
    ...faces.flatMap((f) => [{ P: f.P, Q: f.apex, face: f }, { P: f.Q, Q: f.apex, face: f }]),
].map((s) => ({ ...s, l: Math.hypot(s.Q.x - s.P.x, s.Q.y - s.P.y) }));

/**
 * Codage : les segments de même longueur portent le même nombre de traits (1,
 * 2, 3 dans l'ordre d'apparition, la base d'abord) ; un segment seul de sa
 * longueur n'en porte aucun.
 */
const traitsPatron185 = (segs) => {
    const cle = (l) => l.toFixed(6);
    const effectif = {};
    segs.forEach((s) => { effectif[cle(s.l)] = (effectif[cle(s.l)] || 0) + 1; });
    const rang = {};
    segs.forEach((s) => { const k = cle(s.l); if (effectif[k] >= 2 && !(k in rang)) rang[k] = Object.keys(rang).length + 1; });
    return segs.map((s) => rang[cle(s.l)] || 0);
};

/** nb petits traits perpendiculaires au milieu du segment [p, q] (coordonnées écran). */
const traitsSvg185 = (p, q, nb) => {
    const len = Math.hypot(q.x - p.x, q.y - p.y) || 1;
    const u = { x: (q.x - p.x) / len, y: (q.y - p.y) / len };
    const m = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
    return Array.from({ length: nb }, (_, j) => {
        const o = (j - (nb - 1) / 2) * 3.5;
        const c = { x: m.x + u.x * o, y: m.y + u.y * o };
        return `<line x1="${R1(c.x + u.y * 5)}" y1="${R1(c.y - u.x * 5)}" x2="${R1(c.x - u.y * 5)}" y2="${R1(c.y + u.x * 5)}" stroke="${NOIR}" stroke-width="1.5"/>`;
    }).join('');
};

const cm185 = (l) => `${String(Math.round(l * 100) / 100).replace('.', ',')} cm`;

/**
 * Patron d'une pyramide dont le sommet est à l'aplomb du centre de la base,
 * DESSINÉ À L'ÉCHELLE (révision 2026-09-24, décisions du prof) : base
 * 'triangle' équilatéral de côté a, 'carre' de côté a, 'rectangle' a × b ;
 * chaque face latérale isocèle d'arête latérale L (cm, comme a et b). Même
 * style que patronPyramide (base bleue, faces blanches, trait #1f2937).
 *   variante : 'correct' | 'manque' | 'recouvre' | 'aretes_inegales' (voir
 *     geometriePatron185 ; L2 pour la dernière) ;
 *   codage : les côtés de même longueur portent les mêmes petits traits ;
 *   mesures : les longueurs écrites, une par longueur de la base (du côté de
 *     la face accrochée, d'abord le côté dont la face manque ou est rabattue :
 *     la place y est libre) et une arête latérale (hors de sa face).
 * Chaque patron remplit son cadre (une échelle commune rendait minuscules les
 * trois autres options dès qu'une arête était longue, vu en PNG).
 * data-patron porte la base, la variante et L : le second chemin du QCM.
 */
export const patronPyramide185 = ({ base = 'carre', a = 4, b = a, L = 5, L2 = null, variante = 'correct', face = 0, codage = true, mesures = false } = {}) => {
    const geo = geometriePatron185({ base, a, b, L, L2, variante, face });
    const E = etenduePatron185({ base, a, b, L, L2, variante, face });
    const vb = 240; const k = (vb / 2 - 30) / E;
    const ecr = (p) => ({ x: vb / 2 + k * p.x, y: vb / 2 + k * p.y });
    const pts = (liste) => liste.map((p) => { const q = ecr(p); return `${R1(q.x)},${R1(q.y)}`; }).join(' ');
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb} ${vb}" width="${vb}" height="${vb}" font-family="system-ui, sans-serif" data-patron="base=${base};variante=${variante};face=${face};L=${L}">`];
    out.push(`<rect x="0" y="0" width="${vb}" height="${vb}" fill="white"/>`);
    out.push(`<polygon points="${pts(geo.B)}" fill="${BLEU}" stroke="${NOIR}" stroke-width="1.6" stroke-linejoin="round"/>`);
    geo.faces.forEach((f) => out.push(`<polygon points="${pts([f.P, f.Q, f.apex])}" fill="none" stroke="${NOIR}" stroke-width="1.4" stroke-linejoin="round"/>`));
    const segs = segmentsPatron185(geo);
    if (codage) {
        const traits = traitsPatron185(segs);
        segs.forEach((s, j) => {
            if (traits[j] > 0) out.push(`<g class="code" data-n="${traits[j]}" data-l="${s.l.toFixed(2)}">${traitsSvg185(ecr(s.P), ecr(s.Q), traits[j])}</g>`);
        });
    }
    if (mesures) {
        /** La longueur de s écrite à côté de son milieu, du côté opposé au point `loinDe`. */
        const ecrire = (s, loinDe) => {
            const p = ecr(s.P); const q = ecr(s.Q);
            const m = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
            const texte = cm185(s.l);
            const len = Math.hypot(q.x - p.x, q.y - p.y) || 1;
            let nx = -(q.y - p.y) / len; let ny = (q.x - p.x) / len;
            if ((loinDe.x - m.x) * nx + (loinDe.y - m.y) * ny > 0) { nx = -nx; ny = -ny; }
            const decalage = 9 + Math.abs(nx) * 3.2 * texte.length + Math.abs(ny) * 5;
            out.push(`<text class="mesure" data-l="${s.l.toFixed(2)}" x="${R1(m.x + nx * decalage)}" y="${R1(m.y + ny * decalage + 4)}" font-size="12" text-anchor="middle" fill="${NOIR}">${texte}</text>`);
        };
        // une longueur par longueur de la base, écrite du côté de la face accrochée ;
        // d'abord le côté dont la face manque ou est rabattue (la place y est libre)
        const centre = ecr({ x: 0, y: 0 });
        const cotes = segs.filter((s) => s.base);
        const libre = variante === 'manque' || variante === 'recouvre' ? face : -1;
        const vues = new Set();
        [...cotes.keys()].sort((u, v) => (v === libre) - (u === libre)).forEach((j) => {
            const cle = cotes[j].l.toFixed(6);
            if (!vues.has(cle)) { vues.add(cle); ecrire(cotes[j], centre); }
        });
        // une arête latérale, hors de sa face : la face de droite si elle est là et dépliée
        const f = geo.faces.find((x) => x.i === 1 && !x.rabattue) || geo.faces.find((x) => !x.rabattue);
        if (f) ecrire(segs.find((x) => x.face === f && x.P === f.Q), ecr(f.P));
    }
    out.push('</svg>');
    return out.join('');
};

/**
 * La face rabattue d'un patron « recouvre » se voit-elle ? Son apex, rabattu
 * sur la base, doit rester loin des sommets (0,2 petit côté) et des côtés (0,1)
 * de la base. Regardé en PNG : sur une base triangulaire, dès que la face est
 * haute, son apex tombe sur le sommet opposé et la face se confond avec la
 * base ; l'élève voit alors deux triangles et répond « il manque une face ».
 */
export const recouvrementVisible185 = ({ base = 'carre', a = 4, b = a, L = 5, face = 0 } = {}) => {
    const { B, faces } = geometriePatron185({ base, a, b, L, variante: 'recouvre', face });
    const { apex } = faces.find((f) => f.rabattue);
    const ref = Math.min(a, b);
    const loinSommets = B.every((v) => Math.hypot(v.x - apex.x, v.y - apex.y) >= 0.2 * ref);
    const loinCotes = B.every((v, i) => i === face || distPointSegment(apex, v, B[(i + 1) % B.length]) >= 0.1 * ref);
    return loinSommets && loinCotes;
};

// ─── Perspective cavalière des pyramides nommées (lettres en KaTeX_Math italique) ───

const VUE_185 = { x: -K_CAV * COS_30, y: -K_CAV * SIN_30, z: 1 };
const projeter185 = (v) => ({ x: v.x + K_CAV * v.z * COS_30, y: -(v.y + K_CAV * v.z * SIN_30) });
const faceVue = (V, f, g) => {
    const u = { x: V[f[1]].x - V[f[0]].x, y: V[f[1]].y - V[f[0]].y, z: V[f[1]].z - V[f[0]].z };
    const w = { x: V[f[2]].x - V[f[0]].x, y: V[f[2]].y - V[f[0]].y, z: V[f[2]].z - V[f[0]].z };
    let n = { x: u.y * w.z - u.z * w.y, y: u.z * w.x - u.x * w.z, z: u.x * w.y - u.y * w.x };
    const c = f.reduce((s, i) => ({ x: s.x + V[i].x / f.length, y: s.y + V[i].y / f.length, z: s.z + V[i].z / f.length }), { x: 0, y: 0, z: 0 });
    if ((c.x - g.x) * n.x + (c.y - g.y) * n.y + (c.z - g.z) * n.z < 0) n = { x: -n.x, y: -n.y, z: -n.z };
    return n.x * VUE_185.x + n.y * VUE_185.y + n.z * VUE_185.z < 0;
};

/**
 * Dessine des solides nommés : `sommets` { nom: {x, y, z} } (y vers le haut, z
 * en profondeur), `traits` [{ a, b, cache, fin, couleur }] (fin : trait fin et
 * gris du cube ; couleur : sinon l'encre), `faces` à teinter [[noms]] ;
 * `codages` [{ a, b, n }] : n petits traits au milieu de [ab] (deux moitiés
 * codées pareil = un milieu, 178) ; lettres placées loin des traits.
 */
const dessinNomme = (sommets, traits, teintes = [], echelle = 60, codages = []) => {
    const noms = Object.keys(sommets);
    const P = {};
    noms.forEach((nm) => { const q = projeter185(sommets[nm]); P[nm] = { x: q.x * echelle, y: q.y * echelle }; });
    const segs = traits.map((t) => ({ a: P[t.a], b: P[t.b], w: t.fin ? 1.2 : 1.8, cache: t.cache, fin: t.fin, couleur: t.couleur }));
    const centre = noms.reduce((s, nm) => ({ x: s.x + P[nm].x / noms.length, y: s.y + P[nm].y / noms.length }), { x: 0, y: 0 });
    const boites = []; const textes = [];
    noms.forEach((nm) => {
        const d = { x: P[nm].x - centre.x, y: P[nm].y - centre.y };
        const pos = placerEtiquette(P[nm], nm, 16, (Math.atan2(d.y, d.x) * 180) / Math.PI, 12, segs, boites);
        boites.push(pos.bx); textes.push({ nm, ...pos });
    });
    const xs = []; const ys = [];
    segs.forEach((t) => [t.a, t.b].forEach((p) => { xs.push(p.x); ys.push(p.y); }));
    boites.forEach((b) => { xs.push(b.x0, b.x1); ys.push(b.y0, b.y1); });
    const M = 8; const ox = M - Math.min(...xs); const oy = M - Math.min(...ys);
    const W = Math.ceil(Math.max(...xs) - Math.min(...xs) + 2 * M); const H = Math.ceil(Math.max(...ys) - Math.min(...ys) + 2 * M);
    const f = (v) => String(Math.round(v * 10) / 10);
    let s = `<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`;
    teintes.forEach((fc) => { s += `<polygon points="${fc.map((nm) => `${f(P[nm].x + ox)},${f(P[nm].y + oy)}`).join(' ')}" fill="#e0e7ff" stroke="none"/>`; });
    segs.forEach((t) => {
        s += `<line x1="${f(t.a.x + ox)}" y1="${f(t.a.y + oy)}" x2="${f(t.b.x + ox)}" y2="${f(t.b.y + oy)}" stroke="${t.couleur || (t.fin ? '#94a3b8' : ENCRE)}" stroke-width="${t.w}" stroke-linecap="round"${t.cache ? ' stroke-dasharray="5 4"' : ''}/>`;
    });
    // Codages de milieu : n petits traits perpendiculaires, au milieu de [ab] (le manuel : « // » sur chaque moitié).
    codages.forEach(({ a, b, n = 1 }) => {
        const A = P[a]; const B = P[b];
        const L = Math.hypot(B.x - A.x, B.y - A.y) || 1;
        const ux = (B.x - A.x) / L; const uy = (B.y - A.y) / L;
        const mx = (A.x + B.x) / 2; const my = (A.y + B.y) / 2;
        for (let k = 0; k < n; k++) {
            const d = (k - (n - 1) / 2) * 4;
            const cx = mx + ux * d; const cy = my + uy * d;
            s += `<line x1="${f(cx - uy * 5 + ox)}" y1="${f(cy + ux * 5 + oy)}" x2="${f(cx + uy * 5 + ox)}" y2="${f(cy - ux * 5 + oy)}" stroke="${ENCRE}" stroke-width="1.6" data-code="milieu" data-segment="${a}${b}"/>`;
        }
    });
    textes.forEach((t) => {
        s += `<text x="${f(t.cx + ox)}" y="${f(t.cy + 0.35 * 16 + oy)}" font-size="16" text-anchor="middle" fill="${ENCRE}"><tspan font-family="${POLICE_MATH}" font-style="italic">${t.nm}</tspan></text>`;
    });
    return { W, H, s, margeEtiquettes: Math.min(...textes.map((t) => t.marge)) };
};

/**
 * Une pyramide nommée en perspective cavalière, sommet à l'aplomb du centre de
 * la base ('carre', 'rectangle' ou 'triangle' équilatéral) ; arêtes cachées en
 * pointillés, calculées face par face ; aucune hauteur ni codage.
 * lettres : les sommets de la base, avant gauche d'abord, puis vers la droite
 * et vers l'arrière. Renvoie aussi les sommets 3D dessinés (second chemin des gens).
 */
export const pyramideNommee185 = ({ base = 'carre', sommet = 'S', lettres = ['A', 'B', 'C', 'D'] }) => {
    const BASES_3D = {
        carre: [[-1, -1], [1, -1], [1, 1], [-1, 1]],
        rectangle: [[-1.5, -0.9], [1.5, -0.9], [1.5, 0.9], [-1.5, 0.9]],
        // triangle équilatéral tourné de 20° : le sommet arrière part à gauche, ses arêtes
        // cachées ne se confondent plus avec l'arête vue [SB] (regardé en PNG ; même choix que pyramide.js)
        triangle: [230, 350, 110].map((deg) => [1.33 * Math.cos((deg * Math.PI) / 180), 1.33 * Math.sin((deg * Math.PI) / 180)]),
    };
    const bas = BASES_3D[base]; const n = bas.length;
    const sommets = {};
    bas.forEach(([x, z], i) => { sommets[lettres[i]] = { x, y: 0, z }; });
    sommets[sommet] = { x: 0, y: base === 'rectangle' ? 2.4 : 2.6, z: 0 };
    const V = [...lettres.slice(0, n).map((l) => sommets[l]), sommets[sommet]];
    const g = V.reduce((s, v) => ({ x: s.x + v.x / V.length, y: s.y + v.y / V.length, z: s.z + v.z / V.length }), { x: 0, y: 0, z: 0 });
    const faces = [[...Array(n).keys()], ...[...Array(n).keys()].map((i) => [i, (i + 1) % n, n])];
    const vues = faces.map((fc) => faceVue(V, fc, g));
    const cache = (a, b) => !faces.some((fc, i) => vues[i] && fc.includes(a) && fc.includes(b));
    const traits = [];
    for (let i = 0; i < n; i++) traits.push({ a: lettres[i], b: lettres[(i + 1) % n], cache: cache(i, (i + 1) % n) });
    for (let i = 0; i < n; i++) traits.push({ a: sommet, b: lettres[i], cache: cache(i, n) });
    const { W, H, s } = dessinNomme(sommets, traits, [], 58);
    const nom = `${sommet}${lettres.slice(0, n).join('')}`;
    const alt = `Une pyramide ${nom} dessinée en perspective cavalière : la base ${lettres.slice(0, n).join('')} en bas, le sommet ${sommet} en haut, les arêtes cachées en pointillés.`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-solide="pyramide;base=${base};sommet=${sommet};lettres=${lettres.slice(0, n).join('')}" role="img" aria-label="${alt}">${s}</svg>`;
    return { svg, alt, sommets };
};

/**
 * La pyramide GABC inscrite dans le cube ABCDEFGH du cours (19Espace1 p. 2) :
 * A avant bas gauche, B avant bas droit, C arrière bas droit, D arrière bas
 * gauche, E, F, G, H au-dessus. Le cube en trait fin gris (les arêtes de D
 * cachées), la pyramide en trait épais, ses deux faces vues teintées, [AC]
 * cachée en pointillés. Ses quatre faces sont des triangles rectangles.
 */
export const pyramideDansCube185 = () => {
    const S = {
        A: { x: 0, y: 0, z: 0 }, B: { x: 2, y: 0, z: 0 }, C: { x: 2, y: 0, z: 2 }, D: { x: 0, y: 0, z: 2 },
        E: { x: 0, y: 2, z: 0 }, F: { x: 2, y: 2, z: 0 }, G: { x: 2, y: 2, z: 2 }, H: { x: 0, y: 2, z: 2 },
    };
    const cube = [['A', 'B'], ['B', 'C'], ['C', 'D'], ['D', 'A'], ['E', 'F'], ['F', 'G'], ['G', 'H'], ['H', 'E'], ['A', 'E'], ['B', 'F'], ['C', 'G'], ['D', 'H']];
    const pyr = [['G', 'A'], ['G', 'B'], ['G', 'C'], ['A', 'B'], ['B', 'C'], ['C', 'A']];
    const cle = (a, b) => [a, b].sort().join('');
    const surPyr = new Set(pyr.map(([a, b]) => cle(a, b)));
    // Le cube : caché si l'arête touche D (le sommet arrière bas gauche). La pyramide : face par face.
    const V = ['G', 'A', 'B', 'C'].map((nm) => S[nm]);
    const g = V.reduce((s, v) => ({ x: s.x + v.x / 4, y: s.y + v.y / 4, z: s.z + v.z / 4 }), { x: 0, y: 0, z: 0 });
    const faces = [[1, 2, 3], [0, 1, 2], [0, 2, 3], [0, 1, 3]];                 // ABC, GAB, GBC, GAC
    const vues = faces.map((fc) => faceVue(V, fc, g));
    const idx = { G: 0, A: 1, B: 2, C: 3 };
    const cachePyr = (a, b) => !faces.some((fc, i) => vues[i] && fc.includes(idx[a]) && fc.includes(idx[b]));
    const traits = [
        ...cube.filter(([a, b]) => !surPyr.has(cle(a, b))).map(([a, b]) => ({ a, b, cache: a === 'D' || b === 'D', fin: true })),
        ...pyr.map(([a, b]) => ({ a, b, cache: cachePyr(a, b) })),
    ];
    const teintes = faces.filter((fc, i) => vues[i]).map((fc) => fc.map((i) => ['G', 'A', 'B', 'C'][i]));
    const { W, H, s } = dessinNomme(S, traits, teintes, 62);
    const alt = 'Un cube ABCDEFGH dessiné en perspective cavalière et, à l\'intérieur, la pyramide GABC : ses arêtes [GA], [GB], [GC], [AB], [BC] et [CA] ; les arêtes cachées sont en pointillés.';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-solide="pyramide;cube=ABCDEFGH;sommet=G;base=ABC" role="img" aria-label="${alt.replace(/'/g, '&#39;')}">${s}</svg>`;
    return { svg, alt, sommets: S };
};

/**
 * 178 « Pyramide dans un pavé » (prof 2026-09-25, d'après le manuel) : un pavé
 * droit nommé, en cm (L en largeur, l en profondeur, h en hauteur), dessiné à
 * l'échelle en trait fin gris, arêtes cachées en pointillés (celles du sommet
 * arrière bas gauche) ; à l'intérieur, une pyramide en bleu dont le sommet est
 * à l'aplomb d'un sommet AVANT de la base (coin 0 : avant gauche, 1 : avant
 * droit) : la hauteur est une arête verticale du pavé. Sa base est le triangle
 * rectangle au coin, bordé par les deux arêtes du sol qui en partent, chacune
 * entière ou coupée en son milieu (`milieux` : 'x' l'arête avant, 'z' l'arête
 * vers l'arrière) ; un milieu est un point nommé, codé par un petit trait sur
 * chaque moitié (deux traits pour le second milieu). Lettres : les huit sommets
 * (bas : avant gauche, avant droit, arrière droit, arrière gauche ; puis le
 * haut dans le même ordre), puis les milieux. Renvoie le nom de la pyramide
 * (sommet puis base), les milieux et les sommets 3D en cm (second chemin).
 */
export const pyramideDansPave178 = ({ L, l, h, lettres, coin = 1, milieux = ['x'] }) => {
    const [A, B, C, D, E, F, G, H, ...M] = lettres;
    const S3 = {
        [A]: { x: 0, y: 0, z: 0 }, [B]: { x: L, y: 0, z: 0 }, [C]: { x: L, y: 0, z: l }, [D]: { x: 0, y: 0, z: l },
        [E]: { x: 0, y: h, z: 0 }, [F]: { x: L, y: h, z: 0 }, [G]: { x: L, y: h, z: l }, [H]: { x: 0, y: h, z: l },
    };
    const coinNom = coin === 0 ? A : B; const autreX = coin === 0 ? B : A; const arriere = coin === 0 ? D : C; const sommet = coin === 0 ? E : F;
    const codages = []; const listeMilieux = []; let im = 0;
    let boutX = autreX; let boutZ = arriere;
    if (milieux.includes('x')) {
        boutX = M[im++]; S3[boutX] = { x: L / 2, y: 0, z: 0 };
        codages.push({ a: A, b: boutX, n: 1 }, { a: boutX, b: B, n: 1 }); listeMilieux.push({ nm: boutX, seg: `${A}${B}` });
    }
    if (milieux.includes('z')) {
        boutZ = M[im++]; S3[boutZ] = { x: coin === 0 ? 0 : L, y: 0, z: l / 2 };
        const n = codages.length ? 2 : 1;
        codages.push({ a: coinNom, b: boutZ, n }, { a: boutZ, b: arriere, n }); listeMilieux.push({ nm: boutZ, seg: `${coinNom}${arriere}` });
    }
    const base = [boutX, coinNom, boutZ];
    // À l'échelle : la plus grande dimension vaut 2,4 unités de dessin.
    const k = 2.4 / Math.max(L, l, h);
    const S = {}; Object.keys(S3).forEach((nm) => { S[nm] = { x: S3[nm].x * k, y: S3[nm].y * k, z: S3[nm].z * k }; });
    const cle = (a, b) => [a, b].sort().join('');
    const pave = [[A, B], [B, C], [C, D], [D, A], [E, F], [F, G], [G, H], [H, E], [A, E], [B, F], [C, G], [D, H]];
    const pyr = [[sommet, base[0]], [sommet, base[1]], [sommet, base[2]], [base[0], base[1]], [base[1], base[2]], [base[2], base[0]]];
    const surPyr = new Set(pyr.map(([a, b]) => cle(a, b)));
    const noms = [sommet, ...base];
    const V = noms.map((nm) => S[nm]);
    const g = V.reduce((s, v) => ({ x: s.x + v.x / 4, y: s.y + v.y / 4, z: s.z + v.z / 4 }), { x: 0, y: 0, z: 0 });
    const faces = [[1, 2, 3], [0, 1, 2], [0, 2, 3], [0, 1, 3]];
    const vues = faces.map((fc) => faceVue(V, fc, g));
    const idx = Object.fromEntries(noms.map((nm, i) => [nm, i]));
    const cachePyr = (a, b) => !faces.some((fc, i) => vues[i] && fc.includes(idx[a]) && fc.includes(idx[b]));
    const BLEU_PYR = '#1d4ed8';
    const traits = [
        ...pave.filter(([a, b]) => !surPyr.has(cle(a, b))).map(([a, b]) => ({ a, b, cache: a === D || b === D, fin: true })),
        ...pyr.map(([a, b]) => ({ a, b, cache: cachePyr(a, b), couleur: BLEU_PYR })),
    ];
    const teintes = faces.filter((fc, i) => vues[i]).map((fc) => fc.map((i) => noms[i]));
    const { W, H: HH, s } = dessinNomme(S, traits, teintes, 62, codages);
    const nom = `${sommet}${base.join('')}`;
    const alt = `Un pavé droit ${lettres.slice(0, 8).join('')} dessiné en perspective cavalière, arêtes cachées en pointillés ; à l'intérieur, la pyramide ${nom} en bleu`
        + (listeMilieux.length ? ` ; ${listeMilieux.map((m) => `${m.nm} est le milieu de [${m.seg}]`).join(', ')}, marqué par de petits traits` : '') + '.';
    const figure3d = Object.keys(S3).map((nm) => `${nm}:${S3[nm].x},${S3[nm].y},${S3[nm].z}`).join('|');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${HH}" width="${W}" height="${HH}" font-family="Arial" data-solide="pave;lettres=${lettres.slice(0, 8).join('')};dims=${L},${l},${h};sommet=${sommet};base=${base.join('')};milieux=${listeMilieux.map((m) => `${m.nm}:${m.seg}`).join(',')}" data-figure3d="${figure3d}" role="img" aria-label="${alt.replace(/'/g, '&#39;')}">${s}</svg>`;
    return { svg, alt, sommets: S3, nom, sommet, base, milieux: listeMilieux };
};
